import React, { useState } from "react";
import { useReactFlow } from "@xyflow/react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import AudioPromptPicker from "@/components/audio/AudioPromptPicker";
import VoiceSelect from "@/components/pages/call-flows/components/VoiceSelect";
import {
  BusinessHolidaysEditor,
  BusinessHoursEditor,
} from "@/components/pages/call-flows/components/BusinessHoursEditor";
import { ActionNodeData, AiTransferTargetKind } from "@/model/interface/CallFlow";
import { CallFlowActionType } from "@/model/interface/CallFlowActionType";
import { MembershipStatus } from "@/enum/MembershipStatus";
import {
  useGetAiAgentsQuery,
  useGetHomeQuery,
  useGetMembersQuery,
  useGetOwnPhoneNumbersQuery,
  useGetTeamsQuery,
} from "@/store/rest_api";
import { isUsE164, toUsE164 } from "@/util/validation";
import { memberName } from "@/util/member";
import NodeMoreOptions from "./NodeMoreOptions";

const ORGANISATION_VOICEMAIL = "ORGANISATION";
const PICKED_MEMBERS = "PICKED_MEMBERS";
const MIN_VOICEMAIL_SECONDS = 10;
const MAX_VOICEMAIL_SECONDS = 600;
const MAX_MENU_KEYS = 20;
const MAX_MENU_REPEATS = 5;
const MIN_RING_SECONDS = 10;
const MAX_RING_SECONDS = 60;
const MIN_ROUNDS = 1;
const MAX_ROUNDS = 10;

const TRANSFER_HINTS: Record<AiTransferTargetKind, string> = {
  NONE: "The agent keeps the caller and offers a callback when they ask for a person.",
  MEMBER: "Rings the teammate's apps. If they miss it or are on a call, the caller reaches their voicemail.",
  PHONE_NUMBER: "The caller moves into that number's call flow, which handles a missed call.",
  NUMBER: "Dials the number from your business line. If nobody answers, the call ends.",
};

function transferKindOf(data: ActionNodeData): AiTransferTargetKind {
  if (data.transferMembershipId) {
    return "MEMBER";
  }
  if (data.transferPhoneNumberId) {
    return "PHONE_NUMBER";
  }
  if (data.transferNumber) {
    return "NUMBER";
  }
  return "NONE";
}

interface ActionNodeFormProps {
  nodeId: string;
  data: ActionNodeData;
}

export default function ActionNodeForm({ nodeId, data }: ActionNodeFormProps) {
  const { updateNodeData } = useReactFlow();
  const { data: home } = useGetHomeQuery();
  const { data: members } = useGetMembersQuery({ status: undefined });
  const { data: removedMembers = [] } = useGetMembersQuery({ status: MembershipStatus.REMOVED });
  const { data: aiAgents } = useGetAiAgentsQuery();
  const { data: phoneNumbers = [] } = useGetOwnPhoneNumbersQuery();
  const { data: teams = [] } = useGetTeamsQuery();
  const [transferKind, setTransferKind] = useState<AiTransferTargetKind>(transferKindOf(data));

  const actionType = data.actionType;
  const activeMembers = (members ?? []).filter((member) => member.status === MembershipStatus.ACTIVE);
  const pickableMemberIds = new Set((members ?? []).map((member) => String(member.id)));
  const memberNameById = new Map(
    [...(members ?? []), ...removedMembers].map((member) => [
      String(member.id),
      memberName(member, ""),
    ]),
  );
  const isRemovedMember = (membershipId?: string) =>
    Boolean(membershipId) && members !== undefined && !pickableMemberIds.has(String(membershipId));
  const removedMemberLabel = (membershipId: string) => memberNameById.get(membershipId) || "Former member";
  const isRingListAction =
    actionType === CallFlowActionType.RING_GROUP || actionType === CallFlowActionType.ROUND_ROBIN;
  const ringMembers = data.ringMembers ?? [];
  const menuKeys = data.menuKeys ?? [];
  const menuKeyLabels = data.menuKeyLabels ?? {};
  const businessHours = data.businessHours ?? { days: {}, holidays: [] };

  const setFields = (fields: Partial<ActionNodeData>) => {
    updateNodeData(nodeId, fields);
  };

  const selectTransferKind = (kind: AiTransferTargetKind) => {
    setTransferKind(kind);
    setFields({ transferMembershipId: "", transferPhoneNumberId: "", transferNumber: "" });
  };

  const fieldId = (name: string) => `${nodeId}-${name}`;

  const loopAndPauseFields = (loopLabel: string, pauseLabel: string) => (
    <div className="grid grid-cols-2 gap-3">
      <div className="space-y-2">
        <Label htmlFor={fieldId("loop")}>{loopLabel}</Label>
        <Input
          id={fieldId("loop")}
          type="number"
          min="1"
          value={data.loop ?? 1}
          onChange={(changeEvent) => setFields({ loop: parseInt(changeEvent.target.value) || 1 })}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor={fieldId("pauseLength")}>{pauseLabel}</Label>
        <Input
          id={fieldId("pauseLength")}
          type="number"
          min="0"
          value={data.pauseLength ?? 0}
          onChange={(changeEvent) => setFields({ pauseLength: parseInt(changeEvent.target.value) || 0 })}
        />
      </div>
    </div>
  );

  const ringTimeoutField = (
    <div className="space-y-2">
      <Label htmlFor={fieldId("timeout")}>Ring timeout (seconds)</Label>
      <Input
        id={fieldId("timeout")}
        type="number"
        min="10"
        max="120"
        value={data.timeoutSeconds ?? 30}
        onChange={(changeEvent) => setFields({ timeoutSeconds: parseInt(changeEvent.target.value) })}
      />
      <p className="text-xs text-muted-foreground">How long to ring before moving to the next step</p>
    </div>
  );

  return (
    <>
      {actionType === CallFlowActionType.SAY_MESSAGE && (
        <>
          <AudioPromptPicker
            label="Message *"
            placeholder="Enter the message to say to callers"
            value={data.prompt}
            voice={data.voice}
            onChange={(prompt) => setFields({ prompt, message: prompt.text ?? "" })}
            onVoiceChange={(voice, language) => setFields({ voice, language })}
          />

          <NodeMoreOptions>{loopAndPauseFields("Repeat (times)", "Pause after (seconds)")}</NodeMoreOptions>
        </>
      )}

      {actionType === CallFlowActionType.CALL_USER && (
        <>
          <div className="space-y-2">
            <Label>Select user *</Label>
            <Select
              value={data.targetMembershipId ?? ""}
              onValueChange={(value) => {
                const member = members?.find((candidate) => String(candidate.id) === value);
                const targetUserName = member ? memberName(member, "") : "";
                setFields({ targetMembershipId: value, targetUserName });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a user to call" />
              </SelectTrigger>
              <SelectContent>
                {members?.map((member) => (
                  <SelectItem key={member.user.id} value={String(member.id)}>
                    {memberName(member)}
                  </SelectItem>
                ))}
                {isRemovedMember(data.targetMembershipId) && (
                  <SelectItem value={String(data.targetMembershipId)} disabled>
                    {removedMemberLabel(String(data.targetMembershipId))}
                    <span className="status-pill-destructive ml-2">Removed</span>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            {isRemovedMember(data.targetMembershipId) && (
              <p className="text-xs text-destructive">
                This person was removed, so calls skip this step. Pick someone else.
              </p>
            )}
          </div>

          {ringTimeoutField}
        </>
      )}

      {actionType === CallFlowActionType.CALL_NUMBER && (
        <>
          <div className="space-y-2">
            <Label htmlFor={fieldId("phoneNumber")}>Phone number *</Label>
            <Input
              id={fieldId("phoneNumber")}
              placeholder="+1 (555) 123-4567"
              value={data.targetPhoneNumber ?? ""}
              onChange={(changeEvent) => setFields({ targetPhoneNumber: changeEvent.target.value })}
            />
            <p className="text-xs text-muted-foreground">Enter with country code (e.g., +1 for US)</p>
          </div>

          {ringTimeoutField}

          <NodeMoreOptions>
            <div className="space-y-2">
              <Label htmlFor={fieldId("callNumberMessage")}>Announcement before dialing</Label>
              <Textarea
                id={fieldId("callNumberMessage")}
                placeholder="Optional message to play before forwarding the call"
                value={data.message ?? ""}
                onChange={(changeEvent) => setFields({ message: changeEvent.target.value })}
                rows={3}
              />
              <p className="text-xs text-muted-foreground">Played to the caller while they wait</p>
            </div>

            <div className="space-y-2">
              <Label>Voice</Label>
              <VoiceSelect
                value={data.voice}
                previewText={data.message}
                onChange={(voice, language) => setFields({ voice, language })}
              />
              <p className="text-xs text-muted-foreground">Press play to hear the announcement in the selected voice</p>
            </div>

            {loopAndPauseFields("Repeat (times)", "Pause before dial (seconds)")}

            <div className="space-y-3 rounded-md border p-3">
              <div>
                <Label>Screening</Label>
                <p className="text-xs text-muted-foreground">
                  The person you forward to hears this and must press 1 to take the call. A voicemail picking up
                  cannot press a key, so the call follows No answer instead of landing in their mailbox.
                </p>
              </div>

              <AudioPromptPicker
                label="What they hear"
                placeholder="Call from %contactName% on %numberName%"
                value={data.whisper}
                voice={data.voice}
                onChange={(whisper) => setFields({ whisper })}
                onVoiceChange={(voice, language) => setFields({ voice, language })}
              />

              <p className="text-xs text-muted-foreground">
                Use %contactName%, %from%, %to% and %numberName% to fill in call details.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Show on their phone</Label>
              <Select
                value={data.forwardCallerIdMode ?? "RECEIVING"}
                onValueChange={(forwardCallerIdMode) => setFields({ forwardCallerIdMode })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="RECEIVING">The number they called (so it reads as a work call)</SelectItem>
                  <SelectItem value="CALLER">The caller's own number (so you can ring them back)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="pr-4">
                <Label>Record this call</Label>
                <p className="text-xs text-muted-foreground">Records the forwarded conversation once connected.</p>
              </div>
              <Switch
                checked={data.forwardRecord ?? false}
                onCheckedChange={(forwardRecord) => setFields({ forwardRecord })}
              />
            </div>
          </NodeMoreOptions>
        </>
      )}

      {actionType === CallFlowActionType.VOICEMAIL && (
        <>
          <AudioPromptPicker
            label="Voicemail greeting *"
            placeholder="Please leave a message after the beep..."
            value={data.prompt}
            voice={data.voice}
            onChange={(prompt) => setFields({ prompt, voicemailGreeting: prompt.text ?? "" })}
            onVoiceChange={(voice, language) => setFields({ voice, language })}
          />

          <div className="space-y-2">
            <Label>Whose voicemail is this</Label>
            <Select
              value={data.targetMembershipId || ORGANISATION_VOICEMAIL}
              onValueChange={(value) =>
                setFields({ targetMembershipId: value === ORGANISATION_VOICEMAIL ? "" : value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ORGANISATION_VOICEMAIL}>The organisation</SelectItem>
                {(members ?? []).map((member) => (
                  <SelectItem key={member.id} value={member.id}>
                    {memberName(member)}
                  </SelectItem>
                ))}
                {isRemovedMember(data.targetMembershipId) && (
                  <SelectItem value={String(data.targetMembershipId)} disabled>
                    {removedMemberLabel(String(data.targetMembershipId))}
                    <span className="status-pill-destructive ml-2">Removed</span>
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {isRemovedMember(data.targetMembershipId)
                ? "This person was removed, so owners and admins get the notice instead. Pick someone else."
                : "The owner is notified when a message is left."}
            </p>
          </div>

          <NodeMoreOptions>
            {data.targetMembershipId && (
              <div className="space-y-2">
                <Label>Greeting to play</Label>
                <Select
                  value={data.voicemailGreetingSource || "STEP"}
                  onValueChange={(voicemailGreetingSource) => setFields({ voicemailGreetingSource })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STEP">The greeting set above</SelectItem>
                    <SelectItem value="OWNER">The owner's own greeting</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Owners record their greeting in their account settings. If they have not recorded one, the greeting
                  above is used instead.
                </p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor={fieldId("voicemailMaxLength")}>Maximum message length (seconds)</Label>
              <Input
                id={fieldId("voicemailMaxLength")}
                type="number"
                min={MIN_VOICEMAIL_SECONDS}
                max={MAX_VOICEMAIL_SECONDS}
                value={data.voicemailMaxLengthSeconds ?? 300}
                onChange={(changeEvent) => setFields({ voicemailMaxLengthSeconds: Number(changeEvent.target.value) })}
              />
              <p className="text-xs text-muted-foreground">Between 10 and 600 seconds. Recording stops at the cap.</p>
            </div>

            <div className="flex items-center justify-between rounded-md border p-3">
              <div className="pr-4">
                <Label>Transcribe messages</Label>
                <p className="text-xs text-muted-foreground">
                  Writes the message out and includes it in the notification. Charged per transcribed minute, and
                  only runs when transcription is on for the organisation.
                </p>
              </div>
              <Switch
                checked={data.voicemailTranscribe ?? false}
                onCheckedChange={(voicemailTranscribe) => setFields({ voicemailTranscribe })}
              />
            </div>
          </NodeMoreOptions>
        </>
      )}

      {isRingListAction && (
        <>
          <div className="space-y-2">
            <div>
              <Label>Who to ring *</Label>
              <p className="text-xs text-muted-foreground">
                {actionType === CallFlowActionType.ROUND_ROBIN
                  ? "Each new call starts with the next person in this list, so the load spreads evenly."
                  : "The first person to answer gets the call and everyone else stops ringing."}
              </p>
            </div>

            <Select
              value={data.ringTeamId || PICKED_MEMBERS}
              onValueChange={(choice) => setFields({ ringTeamId: choice === PICKED_MEMBERS ? "" : choice })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={PICKED_MEMBERS}>People I pick</SelectItem>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    Team: {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {data.ringTeamId ? (
              <p className="text-xs text-muted-foreground">
                Rings the team's members in the team's order. Change who is in it on the Team page.
              </p>
            ) : (
              <div className="nowheel max-h-52 space-y-1 overflow-y-auto rounded-md border p-2">
                {members?.map((member) => {
                  const memberId = String(member.id);
                  const isSelected = ringMembers.includes(memberId);
                  return (
                    <label
                      key={member.user.id}
                      className="flex cursor-pointer items-center gap-2 rounded p-1 hover:bg-muted"
                    >
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={(checked) =>
                          setFields({
                            ringMembers: checked
                              ? [...ringMembers, memberId]
                              : ringMembers.filter((candidate) => candidate !== memberId),
                          })
                        }
                      />
                      <span className="text-sm">{memberName(member)}</span>
                    </label>
                  );
                })}
                {ringMembers
                  .filter((memberId) => isRemovedMember(memberId))
                  .map((memberId) => (
                    <label
                      key={`removed-${memberId}`}
                      className="flex cursor-pointer items-center gap-2 rounded p-1 hover:bg-muted"
                    >
                      <Checkbox
                        checked
                        onCheckedChange={() =>
                          setFields({ ringMembers: ringMembers.filter((candidate) => candidate !== memberId) })
                        }
                      />
                      <span className="text-sm text-muted-foreground">{removedMemberLabel(memberId)}</span>
                      <span className="status-pill-destructive">Removed</span>
                    </label>
                  ))}
              </div>
            )}
          </div>

          {actionType === CallFlowActionType.RING_GROUP && (
            <div className="space-y-2">
              <Label>Ring order</Label>
              <Select
                value={data.ringStrategy ?? "SIMULTANEOUS"}
                onValueChange={(ringStrategy) => setFields({ ringStrategy })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SIMULTANEOUS">Everyone at once</SelectItem>
                  <SelectItem value="SEQUENTIAL">One at a time, in the order above</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          {actionType === CallFlowActionType.ROUND_ROBIN && (
            <div className="flex items-center gap-2">
              <span className="text-sm">Try</span>
              <Input
                className="w-16 text-center"
                type="number"
                min={MIN_ROUNDS}
                max={MAX_ROUNDS}
                value={data.ringRounds ?? 1}
                onChange={(changeEvent) => setFields({ ringRounds: Number(changeEvent.target.value) })}
              />
              <span className="text-sm">people before giving up</span>
            </div>
          )}

          <NodeMoreOptions>
            <div className="space-y-2">
              <Label htmlFor={fieldId("ringSeconds")}>Ring each person for (seconds)</Label>
              <Input
                id={fieldId("ringSeconds")}
                type="number"
                min={MIN_RING_SECONDS}
                max={MAX_RING_SECONDS}
                value={data.ringSeconds ?? 20}
                onChange={(changeEvent) => setFields({ ringSeconds: Number(changeEvent.target.value) })}
              />
            </div>

            {actionType === CallFlowActionType.ROUND_ROBIN && (
              <div className="flex items-center justify-between rounded-md border p-3">
                <div className="pr-4">
                  <Label>Prefer the last person they spoke to</Label>
                  <p className="text-xs text-muted-foreground">
                    A returning caller rings whoever answered them last, if that person is in the list.
                  </p>
                </div>
                <Switch
                  checked={data.ringStickyAgent ?? true}
                  onCheckedChange={(ringStickyAgent) => setFields({ ringStickyAgent })}
                />
              </div>
            )}
          </NodeMoreOptions>
        </>
      )}

      {actionType === CallFlowActionType.ROUTE_TO_OWNER && (
        <div className="space-y-2 rounded-md border p-3 text-sm text-muted-foreground">
          <p>
            Rings the member set as Owner on the caller's contact. Nothing to configure here, the routing follows
            whoever owns the record.
          </p>
          <p>
            Both branches must be connected before you can save: <strong>No owner</strong> for callers with no owner
            set, and <strong>No answer</strong> for when the owner does not pick up. A round robin step is a good
            target for No owner.
          </p>
        </div>
      )}

      {actionType === CallFlowActionType.BUSINESS_HOURS && (
        <>
          <BusinessHoursEditor
            value={businessHours}
            timezone={home?.activeOrganisation?.timezone}
            onChange={(nextSchedule) => setFields({ businessHours: nextSchedule })}
          />

          <NodeMoreOptions>
            <BusinessHolidaysEditor
              value={businessHours}
              onChange={(nextSchedule) => setFields({ businessHours: nextSchedule })}
            />
          </NodeMoreOptions>
        </>
      )}

      {actionType === CallFlowActionType.MENU && (
        <>
          <AudioPromptPicker
            label="Menu prompt *"
            placeholder="Press one for sales, two for support"
            value={data.prompt}
            voice={data.voice}
            onChange={(prompt) => setFields({ prompt })}
            onVoiceChange={(voice, language) => setFields({ voice, language })}
          />

          <div className="space-y-3">
            <div>
              <Label>What the caller can press *</Label>
              <p className="text-xs text-muted-foreground">
                Every key you add becomes a branch at the bottom of this step. Use the plus on a branch to add the
                step that handles it.
              </p>
            </div>

            {menuKeys.length === 0 && (
              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                No keys yet. Add one for each option you offer, such as 1 for sales and 2 for support.
              </p>
            )}

            {menuKeys.map((menuKey, keyIndex) => {
              const isDuplicate = menuKey.length > 0 && menuKeys.indexOf(menuKey) !== keyIndex;
              return (
                <div key={`menu-key-${keyIndex}`} className="space-y-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground">Press</span>
                    <Input
                      className="w-14 text-center"
                      value={menuKey}
                      inputMode="numeric"
                      onChange={(changeEvent) => {
                        const nextMenuKeys = [...menuKeys];
                        nextMenuKeys[keyIndex] = changeEvent.target.value.replace(/\D/g, "");
                        setFields({ menuKeys: nextMenuKeys });
                      }}
                    />
                    <span className="text-sm text-muted-foreground">for</span>
                    <Input
                      className="min-w-0 flex-1"
                      placeholder="Sales"
                      value={menuKeyLabels[menuKey] ?? ""}
                      onChange={(changeEvent) =>
                        setFields({ menuKeyLabels: { ...menuKeyLabels, [menuKey]: changeEvent.target.value } })
                      }
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => setFields({ menuKeys: menuKeys.filter((_, index) => index !== keyIndex) })}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  {isDuplicate && <p className="text-xs text-destructive">Key {menuKey} is already used above</p>}
                </div>
              );
            })}

            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={menuKeys.length >= MAX_MENU_KEYS}
              onClick={() => setFields({ menuKeys: [...menuKeys, ""] })}
            >
              <Plus className="mr-2 h-4 w-4" />
              Add key
            </Button>
          </div>

          <NodeMoreOptions>
            <div className="space-y-3 rounded-md border p-3">
              <div>
                <Label>If the caller presses nothing, or the wrong key</Label>
                <p className="text-xs text-muted-foreground">
                  A wrong key plays the message below and repeats the menu. Running out of tries follows the No input
                  branch.
                </p>
              </div>

              <AudioPromptPicker
                label="Wrong key message"
                placeholder="Sorry, that is not a valid option."
                value={data.invalidPrompt}
                onChange={(invalidPrompt) => setFields({ invalidPrompt })}
              />

              <div className="flex items-center gap-2">
                <span className="text-sm">Repeat the menu</span>
                <Input
                  className="w-14 text-center"
                  type="number"
                  min={0}
                  max={MAX_MENU_REPEATS}
                  value={data.menuRepeatCount ?? 1}
                  onChange={(changeEvent) => setFields({ menuRepeatCount: Number(changeEvent.target.value) })}
                />
                <span className="text-sm">times before giving up</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor={fieldId("menuReturnKey")}>Go back to the previous menu when they press</Label>
              <Input
                id={fieldId("menuReturnKey")}
                className="w-14 text-center"
                value={data.menuReturnKey ?? ""}
                placeholder="9"
                inputMode="numeric"
                onChange={(changeEvent) => setFields({ menuReturnKey: changeEvent.target.value.replace(/\D/g, "") })}
              />
            </div>
          </NodeMoreOptions>
        </>
      )}

      {actionType === CallFlowActionType.HANGUP && (
        <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
          Ends the call as soon as the flow reaches this step. It works well after a final message or as the last
          branch of a menu.
        </div>
      )}

      {actionType === CallFlowActionType.DNC_OPT_OUT && (
        <>
          <div className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
            Adds the caller's number to your Do Not Contact list as Do Not Call, plays the confirmation, and ends
            the call. Reach it from a menu key, such as "press 9 to stop receiving calls".
          </div>

          <AudioPromptPicker
            label="Confirmation message"
            placeholder="You have been removed from our call list and will not receive further calls from us. Goodbye."
            value={data.prompt}
            voice={data.voice}
            onChange={(prompt) => setFields({ prompt })}
            onVoiceChange={(voice, language) => setFields({ voice, language })}
          />
          <p className="text-xs text-muted-foreground">Leave the message empty to use the default wording.</p>
        </>
      )}

      {actionType === CallFlowActionType.AI_AGENT && (
        <>
          <div className="space-y-2">
            <Label>AI agent *</Label>
            <Select value={data.aiAgentId ?? ""} onValueChange={(aiAgentId) => setFields({ aiAgentId })}>
              <SelectTrigger>
                <SelectValue placeholder="Choose an AI agent to handle the call" />
              </SelectTrigger>
              <SelectContent>
                {aiAgents?.map((agent) => (
                  <SelectItem key={agent.id} value={agent.id}>
                    {agent.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The AI agent greets the caller and runs the conversation from its system prompt.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Transfer to</Label>
            <Select
              value={transferKind}
              onValueChange={(kind) => selectTransferKind(kind as AiTransferTargetKind)}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">No transfer</SelectItem>
                <SelectItem value="MEMBER">Teammate</SelectItem>
                <SelectItem value="PHONE_NUMBER">One of our numbers</SelectItem>
                <SelectItem value="NUMBER">Phone number</SelectItem>
              </SelectContent>
            </Select>
            {transferKind === "MEMBER" && (
              <Select
                value={data.transferMembershipId ?? ""}
                onValueChange={(transferMembershipId) => setFields({ transferMembershipId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose a teammate" />
                </SelectTrigger>
                <SelectContent>
                  {activeMembers.map((member) => (
                    <SelectItem key={member.id} value={String(member.id)}>
                      {memberName(member)}
                    </SelectItem>
                  ))}
                  {isRemovedMember(data.transferMembershipId) && (
                    <SelectItem value={String(data.transferMembershipId)} disabled>
                      {removedMemberLabel(String(data.transferMembershipId))}
                      <span className="status-pill-destructive ml-2">Removed</span>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            )}
            {transferKind === "PHONE_NUMBER" && (
              <Select
                value={data.transferPhoneNumberId ?? ""}
                onValueChange={(transferPhoneNumberId) => setFields({ transferPhoneNumberId })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose one of your numbers" />
                </SelectTrigger>
                <SelectContent>
                  {phoneNumbers.map((phoneNumber) => (
                    <SelectItem key={phoneNumber.id} value={phoneNumber.id}>
                      {phoneNumber.alias ? `${phoneNumber.alias} (${phoneNumber.number})` : phoneNumber.number}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {transferKind === "NUMBER" && (
              <>
                <Input
                  id={fieldId("transferNumber")}
                  type="tel"
                  placeholder="+1 555 010 0199"
                  value={data.transferNumber ?? ""}
                  onChange={(changeEvent) => setFields({ transferNumber: changeEvent.target.value })}
                  onBlur={() => setFields({ transferNumber: toUsE164(data.transferNumber ?? "") })}
                />
                {data.transferNumber && !isUsE164(data.transferNumber) && (
                  <p className="text-xs text-destructive">Enter a US number, for example +1 555 010 0199.</p>
                )}
              </>
            )}
            <p className="text-xs text-muted-foreground">{TRANSFER_HINTS[transferKind]}</p>
          </div>

          <NodeMoreOptions>
            <div className="space-y-2">
              <Label htmlFor={fieldId("callScope")}>Call scope (optional)</Label>
              <Textarea
                id={fieldId("callScope")}
                placeholder="Extra per-call context added to the agent's system prompt. Leave empty to use the agent defaults."
                value={data.callScope ?? ""}
                onChange={(changeEvent) => setFields({ callScope: changeEvent.target.value })}
                rows={3}
              />
            </div>
          </NodeMoreOptions>
        </>
      )}
    </>
  );
}
